import { ChartUnit } from '@client/graphql/__generated__/types';
import {
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
} from 'recharts';
import { indigoColors } from '~/common/theme/indigo.colors';
import { formatPercent } from '~/common/utils/formatter';
import { getValueFormatter } from './chart.utils';
import { MetricChartProps } from './types';

const MAX_TICK_COUNT = 5;

export const MetricLineChart = ({
  data,
  verticalReferenceLine,
  labelUnit,
  valueUnit,
}: MetricChartProps) => {
  const convertedData =
    labelUnit !== ChartUnit.STRING
      ? data?.map((item) => ({
          ...item,
          label: !!item.label ? Number(item.label) : null, // convert label to number
        }))
      : data;

  return (
    <ResponsiveContainer height="100%" width="100%">
      <LineChart data={convertedData} margin={{ right: 20, left: 25, top: 20 }}>
        {valueUnit === ChartUnit.PERCENT && (
          <ReferenceLine
            isFront
            label={{
              position: 'left',
              fill: indigoColors.whiteAlpha[500],
              value: formatPercent(0),
            }}
            stroke={indigoColors.whiteAlpha[500]}
            strokeDasharray="3 3"
            y={0}
          />
        )}
        {!!verticalReferenceLine && (
          <ReferenceLine
            label={{
              position: 'top',
              fill: indigoColors.whiteAlpha[700],
              value: `${verticalReferenceLine.label}: ${getValueFormatter({
                unit: labelUnit,
              })?.(verticalReferenceLine.value, 0)}`,
            }}
            stroke={indigoColors.whiteAlpha[500]}
            x={verticalReferenceLine.value}
          />
        )}
        <Line
          dataKey="value"
          label={{
            position: 'top',
            formatter: getValueFormatter({ unit: valueUnit }),
            fill: indigoColors.purple[500],
            fontSize: 14,
          }}
          stroke={indigoColors.purple[500]}
          type="natural"
        />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 14 }}
          tickCount={MAX_TICK_COUNT}
          tickFormatter={getValueFormatter({
            unit: labelUnit,
            length: Math.min(data?.length ?? 0, MAX_TICK_COUNT),
          })}
          type={labelUnit !== ChartUnit.STRING ? 'number' : 'category'}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};
